import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
  LabeledButton,
  LabeledButtonContent,
  LabeledButtonLabel,
} from "@babackapp/customer-ui/components/button";
import { DateSelect } from "@babackapp/customer-ui/components/date-select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@babackapp/customer-ui/components/dropdown-menu";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import { Label } from "@babackapp/customer-ui/components/label";
import {
  Option,
  OptionAction,
  OptionContent,
  OptionDescription,
  OptionIcon,
  OptionsList,
  OptionTitle,
} from "@babackapp/customer-ui/components/option";
import {
  RadioGroup,
  RadioGroupItem,
} from "@babackapp/customer-ui/components/radio-group";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { EnterIcon } from "@radix-ui/react-icons";
import {
  ArrowLeft,
  ChevronsUpDown,
  MailboxIcon,
  StoreIcon,
} from "lucide-react";
import { Controller } from "react-hook-form";
import { Link } from "react-router-dom";
import { useReturnModeForm } from "../hooks/use-return-mode-form";

export function ReturnMode() {
  const { form, onSubmit, availableReturnModes, selectableStores } =
    useReturnModeForm();
  const selected = form.watch("returnMode");

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>Mode de retour</HeaderTitle>
        <HeaderDescription>
          Comment souhaitez vous nous faire parvenir votre retour ? Sélectionnez
          un mode de retour et suivez les instructions pour finaliser votre
          demande de retour.
        </HeaderDescription>
      </Header>

      <OptionsList>
        <Controller
          control={form.control}
          name="returnMode"
          render={({ field }) => (
            <RadioGroup onValueChange={field.onChange} value={field.value}>
              {availableReturnModes.includes("carrier") && (
                <Label htmlFor="carrier">
                  <Option>
                    <OptionIcon>
                      <MailboxIcon />
                    </OptionIcon>
                    <OptionTitle>Faire mon retour par voie postale</OptionTitle>
                    <OptionDescription>
                      Imprimez votre étiquette, et remettez votre article au
                      transporteur
                    </OptionDescription>
                    <OptionContent>
                      {selected === "carrier" && (
                        <Controller
                          control={form.control}
                          name="pickupDate"
                          render={({ field: dateField }) => (
                            <DateSelect
                              className="w-full"
                              selected={
                                dateField.value
                                  ? new Date(dateField.value)
                                  : undefined
                              }
                              onSelect={(date) =>
                                dateField.onChange(
                                  date ? date.toISOString().split("T")[0] : undefined,
                                )
                              }
                            >
                              <LabeledButton variant="outline">
                                <LabeledButtonLabel>
                                  Date de retrait
                                </LabeledButtonLabel>
                                <LabeledButtonContent>
                                  {dateField.value
                                    ? new Date(dateField.value).toLocaleDateString("fr-FR")
                                    : "Aucune date sélectionnée"}
                                </LabeledButtonContent>
                                <ChevronsUpDown />
                              </LabeledButton>
                            </DateSelect>
                          )}
                        />
                      )}
                    </OptionContent>
                    <OptionAction>
                      <RadioGroupItem value="carrier" id="carrier" />
                    </OptionAction>
                  </Option>
                </Label>
              )}

              {availableReturnModes.includes("store") && (
                <Label htmlFor="store">
                  <Option>
                    <OptionIcon>
                      <StoreIcon />
                    </OptionIcon>
                    <OptionTitle>Déposer mon retour en boutique</OptionTitle>
                    <OptionDescription>
                      Apporter votre retour dans une de nos boutiques et
                      bénéficiez du retour gratuit. Vous ne savez pas
                    </OptionDescription>
                    <OptionAction>
                      <RadioGroupItem value="store" id="store" />
                    </OptionAction>
                    <OptionContent>
                      {selected === "store" && (
                        <Controller
                          control={form.control}
                          name="storeId"
                          render={({ field: storeField }) => (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <LabeledButton variant="outline">
                                  <LabeledButtonLabel>
                                    Boutique de retour
                                  </LabeledButtonLabel>
                                  <LabeledButtonContent>
                                    {storeField.value
                                      ? selectableStores.find(
                                          (s) => s.id === storeField.value,
                                        )?.name ||
                                        "Boutique sélectionnée"
                                      : "Aucune boutique sélectionnée"}
                                  </LabeledButtonContent>
                                  <ChevronsUpDown />
                                </LabeledButton>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="w-full">
                                <DropdownMenuRadioGroup
                                  value={storeField.value}
                                  onValueChange={storeField.onChange}
                                >
                                  {selectableStores.map((store) => (
                                    <DropdownMenuRadioItem
                                      key={store.id}
                                      value={store.id || ""}
                                    >
                                      <div>
                                        <div className="font-medium">
                                          {store.name}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          {store.address?.address1}, {store.address?.city}
                                        </div>
                                      </div>
                                    </DropdownMenuRadioItem>
                                  ))}
                                </DropdownMenuRadioGroup>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        />
                      )}
                    </OptionContent>
                  </Option>
                </Label>
              )}

              {availableReturnModes.includes("retractation") && (
                <Label htmlFor="retractation">
                  <Option>
                    <OptionIcon>
                      <MailboxIcon />
                    </OptionIcon>
                    <OptionTitle>Exercer mon droit de rétractation</OptionTitle>
                    <OptionDescription>
                      Vous avez le droit de vous rétracter dans les 14 jours
                      suivant votre achat
                    </OptionDescription>
                    <OptionAction>
                      <RadioGroupItem value="retractation" id="retractation" />
                    </OptionAction>
                  </Option>
                </Label>
              )}
            </RadioGroup>
          )}
        />
      </OptionsList>

      <AnimatedButtonRow variants={{}}>
        <AnimatedButtonContainer
          layoutId="line-item-back-button"
          disableVariants={{ exit: true }}
          layout="position"
        >
          <Link to="../summary">
            <Button variant="secondary">
              <ArrowLeft />
              Retour
            </Button>
          </Link>
        </AnimatedButtonContainer>
        <AnimatedButtonContainer
          layoutId="line-item-confirm-button"
          variants={{}}
        >
          <Button onClick={onSubmit}>
            <EnterIcon />
            Confirmer
          </Button>
        </AnimatedButtonContainer>
      </AnimatedButtonRow>
    </WidgetPage>
  );
}
